import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import {
  newId,
  type ReportDetail,
  type ReportOperation,
  type ResolvedData,
  type TableWidget,
  type Widget
} from '@tallyframe/core';
import {
  ReportDetailsInspector,
  ReportEditor,
  ReportViewer,
  WidgetInspector,
  WidgetPalette
} from '@tallyframe/renderer';
import { queryKeys } from '../api/queries';
import {
  useRefreshReport,
  useReportData,
  useReportDetail,
  useReportPersistence,
  useReportRevisionLoader,
  useRestoreRevision
} from '../api/report-queries';
import { useSession } from '../app/session-context';
import { upgradeHref } from '../components/PlanLimitMessage';
import type { AppConfig } from '../api/types';
import { ApiError } from '../api/errors';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/Dialog';
import { InputField } from '../ui/Input';
import { StatusState } from '../ui/StatusState';
import { EditorShell } from '../shell/EditorShell';
import { TopBarTitle } from '../shell/TopBar';
import { toReportBrand, useBrand } from '../brandContext';
import { AwaitingDataPanel } from '../reports/AwaitingDataPanel';
import { DataPanel } from '../reports/DataPanel';
import { HistoryDrawer } from '../reports/HistoryDrawer';
import { EditorToolbar } from '../reports/EditorToolbar';
import { useIsPhone } from '../ui/useIsPhone';
import { ExportMenu } from '../reports/ExportMenu';
import { ReportBanners } from '../reports/ReportBanners';
import { ShareControl } from '../reports/ShareControl';
import type { ExportStatus } from '../reports/useExport';
import { useTitleDraft } from '../reports/useTitleDraft';
import { useUndoShortcuts } from '../reports/useUndoShortcuts';
import { useVersionPreview } from '../reports/useVersionPreview';
import { useReportEditing } from '../editing/useReportEditing';
import { useDocumentTitle } from '../useDocumentTitle';

const reportApi = getRouteApi('/w/$workspaceId/reports/$reportId');

type ReportSearch = ReturnType<typeof reportApi.useSearch>;

const RESTORE_MESSAGE =
  'This replaces the current layout with this version. Your data is not affected, and the current layout stays in History.';

/** The linkable editor state (spec Routes). Every write replaces, so Back
 *  leaves the report instead of stepping back through toggles. */
function useReportSearch(): [ReportSearch, (patch: Partial<ReportSearch>) => void] {
  const search = reportApi.useSearch();
  const navigate = reportApi.useNavigate();
  const setSearch = useCallback(
    (patch: Partial<ReportSearch>) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false }),
    [navigate]
  );
  return [search, setSearch];
}

function loadErrorMessage(error: unknown): string {
  return ApiError.isApiError(error) && error.code !== 'INTERNAL'
    ? error.message
    : 'Something went wrong on our end. Try again.';
}

function LoadError(props: { error: unknown; onRetry(): void }): ReactElement {
  return (
    <StatusState
      kind="error"
      page
      title="This report could not be loaded"
      message={loadErrorMessage(props.error)}
      action={<Button onPress={props.onRetry}>Try again</Button>}
    />
  );
}

/**
 * The report workspace.
 *
 * Palette on the left, the report on the desk in the middle, the inspector on
 * the right — and, always visible in the editor bar, the two pieces of state a
 * person needs to trust what they are looking at: whether their edits are
 * saved, and how old the data is (the Data panel, below the inspector).
 *
 * Undo/redo lives here rather than in the renderer because the renderer holds
 * no state worth undoing: it emits operations and re-syncs from props, so
 * restoring a previous definition is just handing it a different prop.
 */
export function ReportRoute(props: {
  reportId: string;
  /** Supplies `upgradeUrl` for the plan-limit message in the upload dialog. */
  config?: AppConfig | null;
}): ReactElement {
  const { snapshot } = useSession();
  const workspaceId = snapshot.workspaceId ?? '';
  const detail = useReportDetail(workspaceId, props.reportId);
  const { refetch } = detail;
  // Joins a refetch already in flight (a mutation's invalidation) instead of starting a second one.
  const reloadDetail = useCallback(() => void refetch({ cancelRefetch: false }), [refetch]);

  if (!detail.data) {
    return detail.isError ? (
      <LoadError error={detail.error} onRetry={() => void refetch()} />
    ) : (
      <StatusState kind="loading" page title="Loading report…" />
    );
  }

  // A report with no active snapshot has no data to fetch (the data call
  // would answer NOT_FOUND); it opens on the awaiting-data screen.
  if (!detail.data.freshness.activeSnapshotId) {
    return (
      <EditorShell toolbarLabel="Report" toolbar={null}>
        <AwaitingDataPanel
          detail={detail.data}
          upgradeUrl={upgradeHref(props.config?.upgradeUrl ?? null, snapshot)}
          onUploaded={reloadDetail}
        />
      </EditorShell>
    );
  }

  // Keyed by report: navigating from one report to another mounts the
  // workspace fresh, so selection, the refresh error and any half-typed
  // title (see `useTitleDraft`) start over rather than carrying across.
  return (
    <LoadedReport
      key={detail.data.report.id}
      workspaceId={workspaceId}
      detail={detail.data}
      googleSheets={props.config?.googleSheets ?? null}
      onPublicationChanged={reloadDetail}
    />
  );
}

/** A report with data: loads the data, reads the URL state, then hands both to the workspace. */
function LoadedReport(props: {
  workspaceId: string;
  detail: ReportDetail;
  googleSheets: { apiKey: string; appId: string } | null;
  onPublicationChanged(): void;
}): ReactElement {
  const { workspaceId, detail } = props;
  const phone = useIsPhone();
  const reportId = detail.report.id;
  const data = useReportData(workspaceId, reportId);
  const queryClient = useQueryClient();
  const [search, setSearch] = useReportSearch();

  const onDetail = useCallback(
    (next: ReportDetail) => {
      queryClient.setQueryData(queryKeys.report(workspaceId, reportId), next);
    },
    [queryClient, workspaceId, reportId]
  );
  const { refetch } = data;
  const reloadData = useCallback(async () => {
    const result = await refetch({ cancelRefetch: false });
    if (result.error) throw result.error;
  }, [refetch]);

  const onModeChange = useCallback(
    (mode: 'edit' | 'view') => setSearch({ mode: mode === 'view' ? 'preview' : undefined }),
    [setSearch]
  );
  const onHistoryOpenChange = useCallback(
    (open: boolean) => setSearch({ panel: open ? 'history' : undefined }),
    [setSearch]
  );
  const onVersionChange = useCallback((version: number | undefined) => setSearch({ version }), [setSearch]);

  if (!data.data) {
    return data.isError ? (
      <LoadError error={data.error} onRetry={() => void refetch()} />
    ) : (
      <StatusState kind="loading" page title="Loading report…" />
    );
  }

  return (
    <Workspace
      detail={detail}
      data={data.data}
      onDetail={onDetail}
      reloadData={reloadData}
      // Phones view and share; building and editing happen on a larger screen (or by an agent).
      mode={phone || search.mode === 'preview' ? 'view' : 'edit'}
      onModeChange={onModeChange}
      historyOpen={search.panel === 'history'}
      onHistoryOpenChange={onHistoryOpenChange}
      urlVersion={search.version}
      onVersionChange={onVersionChange}
      onPublicationChanged={props.onPublicationChanged}
      shareOnLoad={search.share === 1}
      googleSheets={props.googleSheets}
      googleError={search.google_error}
    />
  );
}

interface WorkspaceProps {
  /** The URL carried `share=1`: open the Share dialog once. */
  shareOnLoad?: boolean;
  detail: ReportDetail;
  data: ResolvedData;
  /** Writes a detail this screen produced (a save, restore or refresh) into the cache. */
  onDetail(detail: ReportDetail): void;
  /** Re-reads the report's data; rejects when that fails. */
  reloadData(): Promise<void>;
  mode: 'edit' | 'view';
  onModeChange(mode: 'edit' | 'view'): void;
  historyOpen: boolean;
  onHistoryOpenChange(open: boolean): void;
  /** The version named in the URL; opened as a preview on arrival when History is open. */
  urlVersion: number | undefined;
  onVersionChange(version: number | undefined): void;
  onPublicationChanged(): void;
  googleSheets: { apiKey: string; appId: string } | null;
  googleError: string | undefined;
}

function Workspace(props: WorkspaceProps): ReactElement {
  const { data, detail, mode, historyOpen, onHistoryOpenChange, onVersionChange, reloadData } = props;
  const phone = useIsPhone();
  const { brand, themeView } = useBrand();
  // Memoized: the renderer keys its chart work on the theme object's identity.
  const reportBrand = useMemo(() => toReportBrand(brand, themeView), [brand, themeView]);

  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [headerSelected, setHeaderSelected] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<ApiError | null>(null);

  useDocumentTitle(detail.report.definition.title);

  // --- history / version preview ------------------------------------------
  // Bumped by every call to `setDetail` below, so the drawer knows to go
  // re-read page one — its own restores, but just as much an autosave or a
  // conflict catch-up this route triggers on its own. Wrapping `onDetail`
  // once, rather than bumping at each call site, is what keeps that true
  // without anyone having to remember to do it by hand.
  const [changeCounter, setChangeCounter] = useState(0);
  const onDetailProp = props.onDetail;
  const setDetail = useCallback(
    (next: ReportDetail) => {
      onDetailProp(next);
      setChangeCounter((count) => count + 1);
    },
    [onDetailProp]
  );

  const [confirmingRestore, setConfirmingRestore] = useState(false);

  const editorRef = useRef<HTMLDivElement | null>(null);
  const previewBannerRef = useRef<HTMLDivElement | null>(null);

  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const persistence = useReportPersistence(workspaceId);
  const editing = useReportEditing({ detail, onDetail: setDetail, persistence });

  const { applyOps, definition } = editing;

  const loadRevision = useReportRevisionLoader(workspaceId, detail.report.id);
  const { mutateAsync: restoreRevision } = useRestoreRevision(workspaceId);
  const { mutateAsync: refreshReport } = useRefreshReport(workspaceId);
  const versions = useVersionPreview({
    reportId: detail.report.id,
    editing,
    loadRevision: (_reportId, revision) => loadRevision(revision),
    restore: (reportId, revision, expectedRevision) => restoreRevision({ reportId, revision, expectedRevision }),
    reload: persistence.load,
    onDetail: setDetail
  });
  const { preview, previewError, restoreNotice, restoring, canRestore } = versions;

  const { backToCurrent: closePreview, cancelPending } = versions;
  const backToCurrent = useCallback(() => {
    closePreview();
    setConfirmingRestore(false);
  }, [closePreview]);

  const focusHistorySwitch = useCallback(() => {
    editorRef.current
      ?.querySelector<HTMLElement>('[role="radiogroup"][aria-label="Side panel"] input[value="history"]')
      ?.focus();
  }, []);

  const closeHistory = useCallback(() => {
    // A preview fetch requested from a row in this drawer must not land
    // after the drawer itself has closed.
    cancelPending();
    onHistoryOpenChange(false);
    // Returns focus to the History radio that opened the drawer — covers both
    // paths that reach here: choosing Inspector in the switch (where focus is
    // already in it) and the drawer's own Close button, which is the one
    // that actually needs it moved.
    focusHistorySwitch();
  }, [cancelPending, onHistoryOpenChange, focusHistorySwitch]);

  // A version named in the URL opens as a preview on arrival, once.
  const arrivalVersionRef = useRef(historyOpen ? props.urlVersion : undefined);
  const { open: openVersion } = versions;
  useEffect(() => {
    const revision = arrivalVersionRef.current;
    arrivalVersionRef.current = undefined;
    if (revision === undefined) return;
    void loadRevision(revision).then(
      (record) => openVersion(revision, record.createdAt),
      // Opening again reports the failure the way a click in History does.
      () => openVersion(revision, '')
    );
    // Arrival only: later URL writes come from this screen's own state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tables = definition.widgets.filter((widget): widget is TableWidget => widget.type === 'table');

  const columnLabels = useMemo(
    () => Object.fromEntries(detail.dataset.columns.map((column) => [column.id, column.label])),
    [detail.dataset.columns]
  );

  // --- operations ---------------------------------------------------------
  const handleOperations = useCallback(
    (operations: ReportOperation[]) => {
      applyOps(operations);
      // The palette mints the widget id before the operation leaves it, so the
      // new widget can be selected without waiting for a round trip or diffing
      // two definitions.
      const added = operations.find((op) => op.op === 'add_widget');
      if (added && added.op === 'add_widget' && added.widget.id) {
        setSelectedWidgetId(added.widget.id);
        setHeaderSelected(false);
      }
    },
    [applyOps]
  );

  const handleSelectWidget = useCallback((widgetId: string | null) => {
    setSelectedWidgetId(widgetId);
    setHeaderSelected(false);
  }, []);

  const handleSelectHeader = useCallback(() => {
    setHeaderSelected(true);
    setSelectedWidgetId(null);
  }, []);

  const handleDelete = useCallback(
    (widgetId: string) => {
      applyOps([{ op: 'remove_widget', widgetId }]);
      setSelectedWidgetId(null);
    },
    [applyOps]
  );

  const handleDuplicate = useCallback(
    (widgetId: string) => {
      // The id is minted here, not on the server: the same operation object is
      // applied locally and remotely, so it has to name the same widget in both.
      const newWidgetId = newId('w');
      applyOps([{ op: 'duplicate_widget', widgetId, newWidgetId }]);
      setSelectedWidgetId(newWidgetId);
      setHeaderSelected(false);
    },
    [applyOps]
  );

  const selectedWidget: Widget | null = useMemo(
    () => definition.widgets.find((widget) => widget.id === selectedWidgetId) ?? null,
    [definition.widgets, selectedWidgetId]
  );

  // --- title --------------------------------------------------------------
  const commitTitle = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed.length === 0 || trimmed === definition.title) return;
      applyOps([{ op: 'rename_report', title: trimmed.slice(0, 200) }]);
    },
    [applyOps, definition.title]
  );

  const title = useTitleDraft({ reportId: detail.report.id, title: definition.title, commit: commitTitle });

  // The canvas on screen during a version preview is read-only history, not
  // the editable definition undo/redo would act on.
  useUndoShortcuts({ undo: editing.undo, redo: editing.redo, disabled: preview !== null });

  // Moves focus somewhere deliberate whenever what is being previewed
  // changes, rather than leaving it to fall back to <body> — which is what
  // happens by default when the element that had it (Back to current,
  // Confirm) unmounts as part of the same update. The URL follows the
  // preview: its version while one is on screen, nothing once it closes.
  const prevPreviewRevisionRef = useRef<number | null>(null);
  useEffect(() => {
    const prevRevision = prevPreviewRevisionRef.current;
    const nextRevision = preview?.revision ?? null;
    prevPreviewRevisionRef.current = nextRevision;
    if (nextRevision !== null && nextRevision !== prevRevision) {
      previewBannerRef.current?.focus();
      onVersionChange(nextRevision);
    } else if (nextRevision === null && prevRevision !== null) {
      focusHistorySwitch();
      onVersionChange(undefined);
    }
  }, [preview, onVersionChange, focusHistorySwitch]);

  // --- refresh ------------------------------------------------------------
  const refresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const refreshed = await refreshReport({
        reportId: detail.report.id,
        expectedActiveSnapshotId: detail.freshness.activeSnapshotId
      });
      setDetail(refreshed);
      editing.adopt(refreshed);
      await reloadData();
    } catch (cause) {
      setRefreshError(
        ApiError.isApiError(cause)
          ? cause
          : new ApiError('INTERNAL', cause instanceof Error ? cause.message : String(cause), 0)
      );
    } finally {
      setRefreshing(false);
    }
  }, [detail.freshness.activeSnapshotId, detail.report.id, editing, refreshReport, reloadData, setDetail]);

  const conflict = editing.saveState === 'conflict';

  const problem =
    editing.rejection ??
    (editing.saveState === 'error' ? editing.saveError : null);

  // The Export menu reports its status; a dismissal is tracked against the
  // status object it dismissed, so the next failure shows again.
  const [exportStatus, setExportStatus] = useState<ExportStatus>({ kind: 'idle' });
  const [dismissedExport, setDismissedExport] = useState<ExportStatus | null>(null);

  const toolbar = (
    <EditorToolbar
      mode={mode === 'view' ? 'preview' : 'edit'}
      onModeChange={(next) => props.onModeChange(next === 'preview' ? 'view' : 'edit')}
      panel={historyOpen ? 'history' : 'inspector'}
      onPanelChange={(next) => (next === 'history' ? onHistoryOpenChange(true) : closeHistory())}
      canUndo={editing.canUndo}
      canRedo={editing.canRedo}
      onUndo={editing.undo}
      onRedo={editing.redo}
      historyLocked={preview !== null}
      freshness={detail.freshness}
      sample={detail.sample === true}
      saveState={editing.saveState}
      share={<ShareControl detail={detail} onChanged={props.onPublicationChanged} openOnLoad={props.shareOnLoad} />}
      exportMenu={
        <ExportMenu
          reportId={detail.report.id}
          tables={tables.map((table) => ({ id: table.id, title: table.title }))}
          onStatus={setExportStatus}
        />
      }
    />
  );

  const editable = mode === 'edit' && preview === null;

  const history = historyOpen ? (
    <HistoryDrawer
      reportId={detail.report.id}
      changeCounter={changeCounter}
      onPreview={(revision, createdAt) => void versions.open(revision, createdAt)}
      onClose={closeHistory}
    />
  ) : undefined;

  const inspector = editable ? (
    <>
      {headerSelected ? (
        <ReportDetailsInspector definition={definition} onOperations={handleOperations} />
      ) : (
        <WidgetInspector
          widget={selectedWidget}
          dataset={detail.dataset}
          onOperations={handleOperations}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
        />
      )}
      <DataPanel
        report={detail.report}
        freshness={detail.freshness}
        refreshing={refreshing}
        refreshError={refreshError}
        onRefresh={() => void refresh()}
        onDismissRefreshError={() => setRefreshError(null)}
        columnLabels={columnLabels}
        link={detail.link ?? null}
        googleSheets={props.googleSheets}
        googleError={props.googleError}
      />
    </>
  ) : undefined;

  return (
    <div className="contents" ref={editorRef}>
      <EditorShell
        toolbarLabel="Report"
        toolbar={toolbar}
        left={editable ? <WidgetPalette dataset={detail.dataset} onOperations={handleOperations} /> : undefined}
        right={history ?? inspector}
      >
        {/* Read-only history is on screen during preview: the title shown here
            is still that of the live report, but nothing typed into it should
            autosave over a definition nobody is looking at. The 200-character
            cap is applied in onChange: InputField's maxLength would show a
            counter in the top bar. */}
        {/* Only 'done': a failure already gets an assertive announcement from the danger banner. */}
        <span className="sr-only" role="status" aria-live="polite">
          {exportStatus.kind === 'done' ? exportStatus.message : ''}
        </span>

        <TopBarTitle>
          {phone ? (
            <span className="m-0 p-0 text-md text-ink truncate">{title.shown}</span>
          ) : (
          <InputField
            label="Report title"
            labelHidden
            fit="content"
            className="min-w-0 max-w-full"
            value={title.shown}
            onChange={(value) => title.onChange(value.slice(0, 200))}
            isDisabled={preview !== null}
            onBlur={title.flush}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                title.flush();
              }
            }}
          />
          )}
        </TopBarTitle>

        <div className="flex flex-col gap-3">
          <ReportBanners
            conflict={conflict ? { onReload: () => void editing.reloadLatest() } : null}
            problem={
              problem
                ? editing.rejection
                  ? { kind: 'rejection', error: problem, onAction: editing.dismissRejection }
                  : { kind: 'save', error: problem, onAction: editing.retrySave }
                : null
            }
            exportError={
              exportStatus.kind === 'error' && dismissedExport !== exportStatus
                ? { message: exportStatus.message, onDismiss: () => setDismissedExport(exportStatus) }
                : null
            }
            previewError={previewError}
            preview={
              preview
                ? {
                    createdAt: preview.createdAt,
                    canRestore,
                    restoring,
                    onRestore: () => {
                      versions.clearRestoreNotice();
                      setConfirmingRestore(true);
                    },
                    onBackToCurrent: backToCurrent
                  }
                : null
            }
            restoreNotice={restoreNotice}
            previewBannerRef={previewBannerRef}
          />

          {preview ? (
            <div data-testid="version-preview">
              <ReportViewer definition={preview.definition} data={data} brand={reportBrand} />
            </div>
          ) : mode === 'view' ? (
            <ReportViewer definition={definition} data={data} brand={reportBrand} />
          ) : (
            <ReportEditor
              definition={definition}
              data={data}
              selectedWidgetId={selectedWidgetId}
              onSelectWidget={handleSelectWidget}
              onOperations={handleOperations}
              brand={reportBrand}
              headerSelected={headerSelected}
              onSelectHeader={handleSelectHeader}
            />
          )}
        </div>
      </EditorShell>

      <ConfirmDialog
        title="Restore this version?"
        message={RESTORE_MESSAGE}
        confirmLabel="Confirm"
        isOpen={confirmingRestore && preview !== null}
        onOpenChange={setConfirmingRestore}
        onConfirm={() => void versions.confirmRestore()}
      />
    </div>
  );
}
